import React, { memo, useMemo, useRef } from 'react';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import { Blok } from '../../interfaces/Blok.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import CTABarSmall, { CTABarSmallBlokProps } from '../CTABarSmall/CTABarSmall';
import { useInstantSearch } from 'react-instantsearch-hooks-web';
import { AlgoliaModifiedQueryData } from '../../interfaces/AlgoliaModifiedQueryData.interface';
import Container from '../Utils/Container/Container';
import Grid from '../Utils/Grid/Grid';
import Col from '../Utils/Col/Col';
import ArticleIndexPageResultCard from '../ArticleIndexPage/ArticleIndexPageResultCard';
import PaginationBar from '../PaginationBar/PaginationBar';
import Heading, { HeadingTypes } from '../Heading/Heading';
import NoSearchResultsFound from '../NoSearchResultsFound/NoSearchResultsFound';
import FlatArticleIndexPageLoading from './FlatArticleIndexPageLoading';

export interface FlatArticleIndexPageResultsBlokProps extends Blok {
  number_of_results_per_page: number;
  cta_bar: CTABarSmallBlokProps[];
  pagination_footer_text: string;
  no_results_found_heading: string;
  no_results_found_body?: string;
  isLoading: boolean;
}

interface FlatArticleIndexPageResultsProps {
  blok: FlatArticleIndexPageResultsBlokProps;
}

const FlatArticleIndexPageResults: React.FC<
  FlatArticleIndexPageResultsProps
> = ({ blok }) => {
  const {
    cta_bar,
    pagination_footer_text,
    no_results_found_heading,
    no_results_found_body,
    isLoading,
  } = blok;

  const {
    results,
    results: { nbHits, hits, hitsPerPage },
  } = useInstantSearch();

  const articleContainer = useRef(null);

  const topRowArticles = useMemo<AlgoliaModifiedQueryData[]>(() => {
    return hits.slice(0, 3);
  }, [hits]);

  const bottomRowArticles = useMemo<AlgoliaModifiedQueryData[]>(() => {
    return hits.slice(3);
  }, [hits]);

  return (
    <section className="py-24 bg-surface/background" ref={articleContainer}>
      <Container>
        {isLoading && <FlatArticleIndexPageLoading />}

        {!isLoading && (
          <>
            {nbHits > 0 && (
              <div className="space-y-10 md:space-y-16 lg:space-y-20">
                <Grid>
                  {topRowArticles.map((article: AlgoliaModifiedQueryData) => (
                    <Col
                      key={article.id}
                      span={{ md: topRowArticles.length === 3 ? 4 : 6 }}
                    >
                      <ArticleIndexPageResultCard article={article} />
                    </Col>
                  ))}
                </Grid>
                {cta_bar.map((bar: CTABarSmallBlokProps) => (
                  <CTABarSmall key={bar._uid} blok={bar} />
                ))}
                <Grid>
                  {bottomRowArticles.map(
                    (article: AlgoliaModifiedQueryData) => (
                      <Col key={article.id} span={{ md: 4 }}>
                        <ArticleIndexPageResultCard article={article} />
                      </Col>
                    )
                  )}
                </Grid>
                {nbHits > hitsPerPage && (
                  <PaginationBar
                    pagination_footer_text={pagination_footer_text}
                    articleContainer={articleContainer}
                  />
                )}
              </div>
            )}
            {nbHits === 0 && (
              <NoSearchResultsFound
                heading={no_results_found_heading}
                body={no_results_found_body}
              />
            )}
          </>
        )}
      </Container>
    </section>
  );
};

export default memo(withBlokWrapper(FlatArticleIndexPageResults, Theme.LIGHT));
